// tabs/HomeTab.jsx — Arriving · Kabat-Zinn–inspired landing page

import React, { useMemo, useState } from "react";

/* =========================== Utilities ============================ */
const cx = (...a) => a.filter(Boolean).join(" ");

/* ============================ Building blocks ======================== */

function CategoryTile({
  icon = "🌱",
  title = "Domain",
  desc = "An area of practice",
  color = "#2563EB",
  onOpen,
}) {
  const bg = `linear-gradient(135deg, ${color}18, #ffffff)`;
  const ring = `${color}55`;

  return (
    <button
      className="card"
      onClick={onOpen}
      style={{
        textAlign: "left",
        padding: 16,
        borderRadius: 18,
        background: bg,
        border: "1px solid var(--line)",
        transition: "box-shadow .15s ease, transform .05s ease",
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = "translateY(1px)")}
      onMouseUp={(e) => (e.currentTarget.style.transform = "translateY(0)")}
      onBlur={(e) => (e.currentTarget.style.transform = "translateY(0)")}
      onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 3px ${ring}`)}
      onBlurCapture={(e) => (e.currentTarget.style.boxShadow = "var(--shadow)")}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
        }}
      >
        <div
          aria-hidden
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: "#fff",
            border: "1px solid var(--line)",
            display: "grid",
            placeItems: "center",
            fontSize: 22,
            boxShadow: "0 6px 14px rgba(2,6,23,.06)",
          }}
        >
          {icon}
        </div>
        <h3 className="h2" style={{ margin: 0 }}>
          {title}
        </h3>
      </div>
      <div className="xs muted">{desc}</div>
    </button>
  );
}

function CategoryGrid({ items = [], onOpenCategory }) {
  return (
    <section
      className="card lg section"
      aria-label="Domains of awareness and practice"
    >
      <div className="section-head">
        <h2 className="h2">Domains of Awareness</h2>
        <span className="pill">Gently explore any tile</span>
      </div>
      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
        }}
      >
        {items.map((it) => (
          <CategoryTile
            key={it.key}
            {...it}
            onOpen={() => onOpenCategory && onOpenCategory(it)}
          />
        ))}
      </div>
    </section>
  );
}

function QuickPromptCard() {
  const PROMPTS = [
    "What are you sensing in your body right now?",
    "Is there anything you are pushing away or clinging to in this moment?",
    "What would it be like to meet this moment with kindness?",
    "Can you let things be exactly as they are for just one breath?",
    "What is asking for your attention, gently, from within?",
  ];
  const [idx, setIdx] = useState(() =>
    Math.floor(Math.random() * PROMPTS.length)
  );
  const prompt = PROMPTS[idx];

  return (
    <section className="card lg section">
      <div className="section-head">
        <h3 className="h2">Today’s Question</h3>
        <button
          className="btn secondary"
          onClick={() => setIdx((i) => (i + 1) % PROMPTS.length)}
        >
          New question
        </button>
      </div>
      <p className="sm" style={{ margin: 0 }}>
        {prompt}
      </p>
    </section>
  );
}

function QuickNote({ title = "Living Practice Journal" }) {
  const [val, setVal] = useState("");
  return (
    <section className="card lg section">
      <div className="section-head">
        <h3 className="h2">{title}</h3>
        <span className="xs muted">
          Not what you achieved — just what you noticed.
        </span>
      </div>
      <textarea
        className="input"
        rows={6}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="What are you becoming aware of in your life lately?"
        aria-label="Living practice journal"
      />
    </section>
  );
}

/* ============================ Page (Arriving) ============================= */

export default function HomeTab({ onOpenCategory }) {
  const CATS = [
    {
      key: "awareness",
      icon: "🧠",
      title: "Awareness & Attention",
      desc: "Noticing the mind without judgment.",
      color: "#3B82F6",
    },
    {
      key: "emotions",
      icon: "🌊",
      title: "Emotions & Mood",
      desc: "Riding the waves instead of fixing the storm.",
      color: "#F97316",
    },
    {
      key: "body",
      icon: "🫁",
      title: "Listening to the Body",
      desc: "Sensation, energy, and embodied wisdom.",
      color: "#22C55E",
    },
    {
      key: "relationships",
      icon: "🤝",
      title: "Connection & Presence",
      desc: "Showing up for others and yourself.",
      color: "#A855F7",
    },
    {
      key: "meaning",
      icon: "❤️",
      title: "Meaning & Heart-Truth",
      desc: "What matters most, right now.",
      color: "#E11D48",
    },
    {
      key: "daily",
      icon: "🧾",
      title: "Integrity in Daily Life",
      desc: "Work, money, and everyday choices.",
      color: "#0EA5E9",
    },
    {
      key: "tech",
      icon: "📱",
      title: "Mindfulness with Devices",
      desc: "Relating wisely to the digital world.",
      color: "#64748B",
    },
    {
      key: "rest",
      icon: "🌙",
      title: "Nourishment & Joy",
      desc: "Rest, play, and simple pleasures.",
      color: "#F973AF",
    },
    {
      key: "change",
      icon: "🧭",
      title: "Navigating Change",
      desc: "Meeting transitions with curiosity.",
      color: "#10B981",
    },
    {
      key: "voice",
      icon: "💬",
      title: "Inner Voice",
      desc: "Befriending the critic inside.",
      color: "#FACC15",
    },
    {
      key: "boundaries",
      icon: "🚧",
      title: "Boundaries & Limits",
      desc: "Respecting what is true for you.",
      color: "#EF4444",
    },
    {
      key: "storm",
      icon: "🆘",
      title: "In the Storm",
      desc: "Anchoring when things feel hard.",
      color: "#7C3AED",
    },
  ];

  const heroImg =
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1800&auto=format&fit=crop";

  return (
    <>
      {/* HERO: Arriving */}
      <section
        className="card lg section"
        style={{ padding: 0, overflow: "hidden" }}
        aria-label="Arriving here"
      >
        <div style={{ position: "relative" }}>
          {/* eslint-disable-next-line */}
          <img
            src={heroImg}
            alt=""
            style={{ width: "100%", height: 260, objectFit: "cover" }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(15,23,42,.35), rgba(15,23,42,.75))",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "flex-end",
              padding: 24,
              color: "#fff",
            }}
          >
            <div>
              <div className="chips" style={{ marginBottom: 8 }}>
                <span
                  className="pill"
                  style={{
                    background: "rgba(255,255,255,.12)",
                    color: "#fff",
                    borderColor: "rgba(255,255,255,.4)",
                  }}
                >
                  Response-Ability · Mindfulness
                </span>
                <span
                  className="pill"
                  style={{
                    background: "rgba(255,255,255,.12)",
                    color: "#fff",
                    borderColor: "rgba(255,255,255,.4)",
                  }}
                >
                  Arriving
                </span>
              </div>
              <h1 className="h1" style={{ margin: 0, color: "#fff" }}>
                Welcome to this moment.
              </h1>
              <div className="sm" style={{ opacity: 0.96, marginTop: 6 }}>
                Before you plan or fix anything, just notice that you are here,
                breathing. This space is for remembering your capacity to
                respond, not react.
              </div>
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="btn secondary"
                  onClick={() => {
                    // hook this into your audio player later
                    alert("Pause. Three slow breaths in your own time.");
                  }}
                >
                  Breathe with me (1 min)
                </button>
                <span className="xs" style={{ opacity: 0.9 }}>
                  “You can’t stop the waves, but you can learn to surf.” &nbsp;—
                  Jon Kabat-Zinn
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Daily reflection */}
      <QuickPromptCard />

      {/* Domains grid */}
      <CategoryGrid
        items={CATS}
        onOpenCategory={(c) => onOpenCategory && onOpenCategory(c)}
      />

      {/* Living practice journal */}
      <QuickNote />
    </>
  );
}
